import React from 'react';
import { SlidersHorizontal } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@cotera/client/v0/components/ui/dropdown-menu';
import { cn } from '@cotera/client/v0/lib/utils';

/** One single-select band of the menu — the chip row it stands in for. */
export type ListFilterGroup = {
  /** Heading above the group. Also the trigger's accessible name, joined. */
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
};

/**
 * The narrow-screen form of a list page's filter band.
 *
 * The band itself is a row of chips — one per type on the Library, one per
 * trigger source on Automations — and it is the right control on a desktop:
 * every option is visible, the current one is obvious, and switching is one
 * click. None of that survives a phone. The chips are wider than the screen, so
 * they scroll horizontally and most of the options are off-edge anyway; and
 * they cost a whole 44px row of a viewport that has about twelve of them,
 * stacked under a title row and above a second row holding the view toggle and
 * the ownership lens. Four rows of chrome before the first result.
 *
 * Behind one trigger the same choices cost 32px on a row that already exists.
 * The trade is real — the options are no longer visible at a glance — but on a
 * screen this size they were not visible anyway, and the lit trigger plus the
 * count in the heading say that something is narrowing the list.
 *
 * Single-select throughout, because that is what the chips are: these bands
 * pick one lens, they do not accumulate. The toolbar's own `filterOptions`
 * menu, next door, is the multi-select one.
 */
export const ListFilterMenu: React.FC<{
  groups: ListFilterGroup[];
  /** Something is narrowing the list, so the trigger reads as engaged. */
  active: boolean;
  onClear?: () => void;
  className?: string;
}> = ({ groups, active, onClear, className }) => (
  <DropdownMenu>
    <DropdownMenuTrigger
      aria-label={`Filter by ${groups
        .map((group) => group.label.toLowerCase())
        .join(' and ')}`}
      className={cn(
        'inline-flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md',
        'text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground',
        active && 'text-foreground',
        className
      )}
    >
      <SlidersHorizontal className="h-4 w-4" />
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="min-w-[12rem]">
      {groups.map((group, index) => (
        <React.Fragment key={group.label}>
          {index > 0 ? <DropdownMenuSeparator /> : null}
          <DropdownMenuLabel className="text-[11px] font-normal text-muted-foreground">
            {group.label}
          </DropdownMenuLabel>
          <DropdownMenuRadioGroup
            value={group.value}
            onValueChange={group.onChange}
          >
            {group.options.map((option) => (
              <DropdownMenuRadioItem
                key={option.value}
                value={option.value}
                className="cursor-pointer"
              >
                {option.label}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </React.Fragment>
      ))}
      {onClear !== undefined && active ? (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem className="cursor-pointer" onSelect={onClear}>
            Clear filters
          </DropdownMenuItem>
        </>
      ) : null}
    </DropdownMenuContent>
  </DropdownMenu>
);
